import { AccountCategory, Provider } from '@fruitful/types';
import React from 'react';
import {
    BodySmallRegular,
    CirclePlusIcon,
    H6Emphasized,
    LabelRegular,
    Shadows,
    SmoothieScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { TextError } from '../../common/components/InlineError';
import { MembershipType } from '../../entities/entityTypes';
import { AccountCreateModal } from '../../entities/membership/components/AccountCreateModal';
import { AccountEditModal } from '../../entities/membership/components/AccountEditModal';
import { MoneyMapperCurrentAssetsAccountRow } from './current-assets/MoneyMapperCurrentAssetsAccountRow';
import {
    CURRENT_ASSETS_ACTIONS_OUTER_WIDTH,
    currentAssetsBalanceColumnProps,
    currentAssetsHolisticCategoryColumnProps,
    currentAssetsInstitutionColumnProps,
    currentAssetsLastFourColumnProps,
    currentAssetsNameColumnProps,
    currentAssetsOwnershipColumnProps,
    currentAssetsTypeColumnProps,
} from './current-assets/moneyMapperCurrentAssetsTableLayout';
import { MoneyMapperManualAccountDetailsModal } from './current-assets/MoneyMapperManualAccountDetailsModal';
import { useMoneyMapperCurrentAssetsSection } from './current-assets/useMoneyMapperCurrentAssetsSection';

export interface MoneyMapperCurrentAssetsSectionProps {
    isSaving: boolean;
}

export const MoneyMapperCurrentAssetsSection = ({
    isSaving,
}: MoneyMapperCurrentAssetsSectionProps) => {
    const {
        membership,
        canOpenAccountMenusAndAdd,
        canEditManualAccountInline,
        isLoadingHolisticSummary,
        isHolisticSummaryError,
        accounts,
        ownerPickerItems,
        holisticCategoryPickerItems,
        onPersistManualAccount,
        onPersistLinkedHolisticCategory,
        onPersistLinkedAccountNickname,
        menuAccount,
        setMenuAccount,
        showCreateManualModal,
        setShowCreateManualModal,
        modalBusy,
    } = useMoneyMapperCurrentAssetsSection({ isSaving });

    if (membership == null) {
        return null;
    }

    const showOwnershipColumn =
        membership.membershipType === MembershipType.JOINT;

    if (isHolisticSummaryError) {
        return <TextError message="Error loading accounts data" />;
    }

    if (isLoadingHolisticSummary) {
        return (
            <YStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                gap={12}
            >
                <Spinner />
                <BodySmallRegular color="$gray1000">
                    Loading accounts…
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack flex={1} minHeight={0}>
            <SmoothieScrollView
                containerStyle={{
                    width: '100%',
                    marginHorizontal: 0,
                    flexGrow: 1,
                    paddingBottom: 16,
                }}
            >
                <YStack gap={20}>
                    <YStack gap={6}>
                        <H6Emphasized color="$gray1200">
                            Current assets
                        </H6Emphasized>
                    </YStack>

                    <YStack
                        backgroundColor="$white"
                        width="100%"
                        borderRadius={24}
                        padding={24}
                        gap={0}
                        {...Shadows.SMALL}
                    >
                        <XStack
                            width="100%"
                            alignItems="flex-end"
                            paddingBottom={12}
                            paddingHorizontal={4}
                            gap={8}
                        >
                            <YStack {...currentAssetsNameColumnProps}>
                                <LabelRegular color="$gray1000">
                                    Account name
                                </LabelRegular>
                            </YStack>
                            <YStack {...currentAssetsInstitutionColumnProps}>
                                <LabelRegular color="$gray1000">
                                    Institution
                                </LabelRegular>
                            </YStack>
                            {showOwnershipColumn && (
                                <YStack {...currentAssetsOwnershipColumnProps}>
                                    <LabelRegular color="$gray1000">
                                        Ownership
                                    </LabelRegular>
                                </YStack>
                            )}
                            <YStack {...currentAssetsTypeColumnProps}>
                                <LabelRegular color="$gray1000">
                                    Type
                                </LabelRegular>
                            </YStack>
                            <YStack
                                {...currentAssetsHolisticCategoryColumnProps}
                            >
                                <LabelRegular color="$gray1000">
                                    Holistic category
                                </LabelRegular>
                            </YStack>
                            <YStack {...currentAssetsLastFourColumnProps}>
                                <LabelRegular color="$gray1000">
                                    Acct number
                                </LabelRegular>
                            </YStack>
                            <YStack {...currentAssetsBalanceColumnProps}>
                                <LabelRegular color="$gray1000">
                                    Balance
                                </LabelRegular>
                            </YStack>
                            <XStack
                                width={CURRENT_ASSETS_ACTIONS_OUTER_WIDTH}
                                flexShrink={0}
                            />
                        </XStack>

                        {accounts.length === 0 ? (
                            <YStack paddingVertical={28} alignItems="center">
                                <BodySmallRegular color="$gray1000">
                                    No accounts yet
                                </BodySmallRegular>
                            </YStack>
                        ) : (
                            accounts.map((account) => (
                                <MoneyMapperCurrentAssetsAccountRow
                                    key={account.id}
                                    account={account}
                                    membership={membership}
                                    canOpenAccountMenusAndAdd={
                                        canOpenAccountMenusAndAdd
                                    }
                                    canEditManualAccountInline={
                                        canEditManualAccountInline
                                    }
                                    moneyMapSaving={isSaving}
                                    modalBusy={modalBusy}
                                    showOwnershipColumn={showOwnershipColumn}
                                    ownerPickerItems={ownerPickerItems}
                                    holisticCategoryPickerItems={
                                        holisticCategoryPickerItems
                                    }
                                    onOpenAccountMenu={setMenuAccount}
                                    onPersistManualAccount={
                                        onPersistManualAccount
                                    }
                                    onPersistLinkedHolisticCategory={
                                        onPersistLinkedHolisticCategory
                                    }
                                    onPersistLinkedAccountNickname={
                                        onPersistLinkedAccountNickname
                                    }
                                />
                            ))
                        )}

                        <XStack
                            width="100%"
                            justifyContent="flex-start"
                            paddingTop={accounts.length === 0 ? 8 : 16}
                            paddingBottom={4}
                        >
                            <Button
                                variant="secondary"
                                size="large"
                                icon={CirclePlusIcon}
                                disabled={
                                    !canOpenAccountMenusAndAdd ||
                                    isSaving ||
                                    modalBusy
                                }
                                onPress={() => {
                                    setShowCreateManualModal(true);
                                }}
                            >
                                Add an asset
                            </Button>
                        </XStack>
                    </YStack>
                </YStack>
            </SmoothieScrollView>

            {menuAccount?.provider === Provider.MANUAL && (
                <MoneyMapperManualAccountDetailsModal
                    account={menuAccount}
                    isOpen
                    onClose={() => {
                        setMenuAccount(null);
                    }}
                />
            )}
            {menuAccount != null &&
                menuAccount.provider !== Provider.MANUAL && (
                    <AccountEditModal
                        account={menuAccount}
                        isOpen
                        onClose={() => {
                            setMenuAccount(null);
                        }}
                    />
                )}

            {showCreateManualModal ? (
                <AccountCreateModal
                    isOpen
                    defaultCategory={AccountCategory.SAVE}
                    onClose={() => {
                        setShowCreateManualModal(false);
                    }}
                />
            ) : null}
        </YStack>
    );
};
